import React, { useState } from 'react';
import { Box, Container, Flex, Heading } from '@chakra-ui/react';
import * as yup from 'yup';
import type { GetServerSideProps, NextPage } from 'next';
import isMobileDevice from 'ismobilejs';
import { SPECIALS } from '../../../common/constants';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import withAuth from '../../../server/libs/withAuth';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import useSpecialPage from '../../../hooks/useSpecialPage';
import FullWidthBox from '../../../components/FullWidthBox';
import {
  PicuCoursesType,
  PicuCourseList,
  PicuEmpty,
  PicuMember,
  PicuMembers,
  PicuTheme,
  PicuThemeSelector,
  PicuTypeSelector,
  PicuTitle,
} from '../../../components/PicuPageItems';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified,
  isPublisherAccount,
} from '../../../common/libs/user';
import { ArticleHeader } from '../../../components/ArticleSummary';
import { Tag } from '../../tagsearch/[tagIds]';
import ArticlesGrid from '../../../components/ArticlesGrid';
import { getArticleIdsWithAndTagIds } from '../../../server/libs/tag';
import prisma from '../../../server/libs/prisma';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { getTagsWithTagSettings } from '../../../common/libs/specials';
import * as date from '../../../server/libs/date';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';

export const setting = {
  specialPageSlug: 'pediatric-intensive-care',
  specialPageShortName: SPECIALS.picu.name,
  specialPageTitle: SPECIALS.picu.alt,
  // specialPageTags: SPECIALS.picu.tags, // プレビューから公開にするときはこちらを有効にする
  specialPageTags: ['PICUのフェロー部屋'],
};

const mainImagePath = SPECIALS.picu.mainImage ?? '';
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内
const specialPageSlug = setting.specialPageSlug;
const specialPagePrimaryTag = setting.specialPageTags[0];

export const picuThemes: PicuTheme[] = [
  {
    name: '呼吸',
    description: '気道確保・呼吸管理に必要な知識と技術を学びます。',
    courses: [
      {
        title: '呼吸器系の正常解剖と成長に伴う変化',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '気道、ガス交換、肺・胸壁の力学、呼吸調節の生理学',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '呼吸モニタリングの原理',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '呼吸器系の画像診断',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title:
          '呼吸器疾患（急性肺障害、閉塞性肺疾患、肺炎など）の病態生理と治療',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '人工呼吸の原理と理論、その他の呼吸サポート',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '人工呼吸からの離脱',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '長期人工呼吸管理の開始と維持',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '小児呼吸器疾患と好発年齢',
        priority: 'A',
        type: 'knowledge',
      },
      {
        title: '緊急外科的気道確保の適応と方法',
        priority: 'B',
        type: 'knowledge',
      },
      {
        title: 'エコー評価：肺（適応と方法）',
        priority: 'C',
        type: 'knowledge',
      },
      {
        title: '気道の評価と維持',
        priority: 'A',
        type: 'skill',
      },
      {
        title: 'バッグ＆マスクによる人工呼吸',
        priority: 'A',
        type: 'skill',
      },
      {
        title: '気管挿管',
        priority: 'A',
        type: 'skill',
      },
      {
        title: '気道確保が困難な場合や失敗した場合の管理',
        priority: 'A',
        type: 'skill',
      },
      {
        title: '既存の気管切開チューブの交換',
        priority: 'A',
        type: 'skill',
      },
      {
        title: '侵襲的換気と非侵襲的換気の使い分け',
        priority: 'A',
        type: 'skill',
      },
      {
        title: 'エコー評価：胸水',
        priority: 'A',
        type: 'skill',
      },
      {
        title: '挿管患者の気管支ファイバー検査',
        priority: 'B',
        type: 'skill',
      },
      {
        title: '高度な人工呼吸サポート（HFO・APRVなど）',
        priority: 'B',
        type: 'skill',
      },
      {
        title: '胸腔穿刺',
        priority: 'C',
        type: 'skill',
      },
      {
        title: '胸腔ドレーン挿入',
        priority: 'C',
        type: 'skill',
      },
    ],
  },
  // 順次、各themeのcourses配列の内容を追加すると公開状態になる
  // 配列の内容は [PICUオンラインコミュニティ]管理シート コース一覧 参照
  { name: '循環', description: '', courses: [] },
  { name: '神経', description: '', courses: [] },
  { name: '腎臓・泌尿器・生殖器', description: '', courses: [] },
  { name: '消化器・肝胆膵', description: '', courses: [] },
  { name: '血液疾患・腫瘍性疾患', description: '', courses: [] },
  { name: '代謝・内分泌', description: '', courses: [] },
  { name: '周術期管理・臓器移植', description: '', courses: [] },
  { name: '外傷・中毒', description: '', courses: [] },
  { name: '敗血症', description: '', courses: [] },
  { name: '患者搬送', description: '', courses: [] },
  { name: '薬物療法', description: '', courses: [] },
  { name: '栄養療法', description: '', courses: [] },
  { name: '終末期医療', description: '', courses: [] },
];

const members: PicuMember[] = [
  {
    name: '中川聡',
    img: '',
    institution: '国立成育医療研究センター',
    department: '集中治療科',
  },
  {
    name: '川口敦',
    img: '',
    institution: '聖マリアンナ医科大学',
    department: 'PICU',
  },
  {
    name: '赤嶺陽子',
    img: '',
    institution: '大阪市立総合医療センター',
    department: '集中治療部',
  },
  {
    name: '井手健太郎',
    img: '',
    institution: '国立成育医療研究センター',
    department: '集中治療科',
  },
];

type Article = ArticleHeader & {
  tags: Tag[];
};

type PageContext = {
  theme: PicuTheme;
  articles: Article[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isCorporationAccount: boolean;
  isLicenseVerified: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

const PicuPage: NextPage<PageContext> = ({
  theme,
  articles,
  isMobile,
  isDemoAccount,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isPrivate,
}) => {
  const { specialPageTitle, specialPageShortName } = setting;

  const [selectedType, setSelectedType] = useState<PicuCoursesType | null>(
    null,
  );

  const hasInactiveTheme = picuThemes.some(({ courses }) => !courses.length);

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath: specialPageSlug,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
  });

  if (isBlocked) {
    return blockedContent;
  }

  const { courses } = theme;

  const coursesArticles = courses.map(({ title }) =>
    articles.filter((article) =>
      article.tags.some((tag: Tag) => tag.name === title),
    ),
  );

  const onClickCourse = (course: string) => {
    const titleElement = document.getElementById(course);
    if (titleElement) {
      window.scrollTo({
        top: titleElement.offsetTop - 56, // ヘッダーの高さ分調整
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white" mb={-16}>
        {breadcrumbs}
        <FullWidthBox>
          <PicuTitle isMobile={isMobile} />
        </FullWidthBox>
        <FullWidthBox
          bgColor="backgroundLight"
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container py={9}>
            <Heading as="h2" textAlign="center" fontSize="2xl" pb={5}>
              ”救える子供たち”のために私たちができること
            </Heading>
            <Flex direction="column" gap={4}>
              {[
                {
                  title: '本企画の目的',
                  content: `国内には評価認証された小児集中治療（PICU）の教育プログラムや専門医制度は確立しておらず、各施設および指導医に委ねられています。
2022年に実施された小児集中治療施設の代表者へのアンケート調査では、カナダの小児集中治療研修プログラムの内容が本邦においても活用できる可能性が示されました（Akamine et al. Pediatrics International. 2022）。
この学ぶべき内容リストの枝に、各施設・各指導医が持つ指導マテリアルを葉や花のようにちりばめることができれば、若手小児集中治療医の学びだけでなく、小児集中治療の裾野を広げて救える子どもたちを増やせると信じています。本企画では、小児集中治療に必要な知識と技術の習得に役立つ資料と場所を提供します。`,
                },
                {
                  title: 'コンテンツ内容',
                  content: `本企画の趣旨にご賛同いただいた医療者より提供されたPICU研修に役立つスライドを共有しています。`,
                },
                {
                  title: '対象者',
                  content: `小児救急集中治療に関わる全ての医療者`,
                },
                ...(hasInactiveTheme
                  ? [
                      {
                        title: '公開日程',
                        content: `2025年10月より、１〜２ヶ月ごとに順次公開予定`,
                      },
                    ]
                  : []),
              ].map(({ title, content }) => (
                <Box key={title}>
                  <Heading as="h3" fontSize={14} pb={1}>
                    {title}
                  </Heading>
                  <Box fontSize={14} whiteSpace="pre-wrap">
                    {content}
                  </Box>
                </Box>
              ))}
            </Flex>
          </Container>
        </FullWidthBox>
        <Box px={4}>
          <Box py={4} id="menu">
            <PicuThemeSelector
              themes={picuThemes}
              currentThemeName={theme.name}
            />
            <Heading as="h3" fontSize={18} mt={4} py={2}>
              領域「{theme.name}」
            </Heading>
            <Box fontSize="xs">{theme.description}【優先度 : A,B,C】</Box>
            <PicuTypeSelector
              onSelectType={setSelectedType}
              selectedType={selectedType}
            />
            <PicuCourseList
              theme={theme}
              onClickCourse={(course) => {
                onClickCourse(course);
              }}
              selectedType={selectedType}
            />
            {courses
              .filter(({ type }) => {
                const isAll = selectedType === null;
                return isAll || type === selectedType;
              })
              .map(({ title }, index) => {
                const articleList = coursesArticles[index];
                const hasArticles = articleList.length > 0;
                return (
                  <Box key={title} id={title} py={6}>
                    <Heading as="h3" fontSize="lg" pb={4}>
                      # {title} に関するスライド
                    </Heading>
                    {hasArticles ? (
                      <ArticlesGrid
                        articles={articleList}
                        isMobile={isMobile}
                        gaEventLabel={`${specialPageShortName}_course`}
                        onShowThumbnail={(articleId) => {
                          sendAnalyticsEvent(
                            'slide_thumbnail',
                            'show',
                            `special_${specialPageShortName}#${articleId}`,
                          );
                        }}
                        onClick={(articleId) => {
                          sendAnalyticsEvent(
                            'slide_thumbnail',
                            'click',
                            `special_${specialPageShortName}#${articleId}`,
                          );
                        }}
                      />
                    ) : (
                      <PicuEmpty
                        onClickContact={() => {
                          sendAnalyticsEvent(
                            'special_contents',
                            'click',
                            `${specialPageShortName}_contact`,
                          );
                        }}
                      />
                    )}
                  </Box>
                );
              })}
          </Box>
        </Box>
        <FullWidthBox bgColor="backgroundLight">
          <Container
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            py={12}
          >
            <Heading as="h3" fontSize="xl" textAlign="center" py={2}>
              運営メンバー
            </Heading>
            <PicuMembers members={members} />
          </Container>
        </FullWidthBox>
      </Box>
    </>
  );
};

export default PicuPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    await createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    const themeName = yup
      .string()
      .default(picuThemes[0].name)
      .validateSync(context.query.theme);

    const theme = picuThemes.find(({ name }) => name === themeName);

    if (!theme || !theme.courses.length) {
      return {
        notFound: true,
      };
    }

    // 該当するテーマの全てのコースのタイトルと、specialPagePrimaryTagを同時に持つ記事を取得するための設定値として配列を生成
    const andTagTexts = theme.courses
      .map(({ title }) => title)
      .map((title) => [specialPagePrimaryTag, title]);

    const { tagsFromAndTagTexts } = await getTagsWithTagSettings({
      tagTexts: [],
      andTagTexts,
    });

    const articleIdsFromAndTagIds = await getArticleIdsWithAndTagIds(
      tagsFromAndTagTexts.map((tags) => tags.map((tag) => tag.id)),
    );
    // 結果を結合し重複を削除
    const articleIdList: string[] = Array.from(
      new Set(articleIdsFromAndTagIds),
    );

    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIdList,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        create_date: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    const articles: Article[] = articleHeaders.reduce(
      (articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: author.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds: author.update_date.getTime(),
            profilePhotoS3Key: author.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      },
      [],
    );

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        theme,
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
